import React from 'react';
import CodeBlock from '../components/CodeBlock';

function SectionHeading({ id, children }) {
  return (
    <h2
      id={id}
      className="font-heading text-2xl font-semibold mt-16 mb-4 scroll-mt-20"
      style={{ color: 'var(--text-primary)' }}
    >
      {children}
    </h2>
  );
}

function SubHeading({ children }) {
  return (
    <h3 className="font-heading text-lg font-medium mt-8 mb-3" style={{ color: 'var(--text-primary)' }}>
      {children}
    </h3>
  );
}

function Paragraph({ children }) {
  return (
    <p className="leading-relaxed mb-4 font-body" style={{ color: 'var(--text-secondary)' }}>
      {children}
    </p>
  );
}

function ParamTable({ params }) {
  return (
    <div className="glass-card overflow-hidden my-4">
      <table className="param-table">
        <thead>
          <tr>
            <th>Parameter</th>
            <th>Type</th>
            <th>Required</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {params.map((p) => (
            <tr key={p.name}>
              <td className="font-mono text-xs" style={{ color: 'var(--accent-primary)', opacity: 0.8 }}>{p.name}</td>
              <td className="font-mono text-xs" style={{ color: 'var(--text-muted)' }}>{p.type}</td>
              <td>
                {p.required ? (
                  <span className="text-xs font-semibold" style={{ color: 'var(--accent-warning)' }}>Yes</span>
                ) : (
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>No</span>
                )}
              </td>
              <td className="text-xs" style={{ color: 'var(--text-secondary)' }}>{p.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusCodes({ codes }) {
  const colorMap = {
    '200': 'var(--accent-success)',
    '201': 'var(--accent-success)',
    '204': 'var(--accent-success)',
    '400': 'var(--accent-warning)',
    '401': 'var(--accent-danger)',
    '403': 'var(--accent-danger)',
    '404': 'var(--accent-warning)',
    '409': 'var(--accent-warning)',
    '422': 'var(--accent-warning)',
    '429': 'var(--accent-warning)',
    '500': 'var(--accent-danger)',
  };

  return (
    <div className="glass-card overflow-hidden my-4">
      <table className="param-table">
        <thead>
          <tr>
            <th>Status</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {codes.map((c) => (
            <tr key={c.code}>
              <td
                className="font-mono text-xs font-semibold"
                style={{ color: colorMap[c.code] || 'var(--text-secondary)' }}
              >
                {c.code}
              </td>
              <td className="text-xs" style={{ color: 'var(--text-secondary)' }}>{c.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function GettingStarted() {
  return (
    <article>
      {/* Page header */}
      <div className="mb-12">
        <p
          className="text-xs font-mono uppercase tracking-widest mb-2"
          style={{ color: 'var(--accent-primary)', opacity: 0.6 }}
        >
          Getting Started
        </p>
        <h1
          className="font-heading font-bold mb-4"
          style={{ fontSize: '32px', fontWeight: 700, color: 'var(--text-primary)' }}
        >
          Getting Started
        </h1>
        <p className="text-lg font-body leading-relaxed max-w-2xl" style={{ color: 'var(--text-secondary)' }}>
          The Noyka Evidence Engine API provides programmatic access to evidence
          management, WORM storage, agent orchestration, and compliance tooling.
          All requests are authenticated and encrypted in transit via TLS 1.3.
        </p>
      </div>

      {/* Base URL */}
      <div className="glass-card p-4 mb-8 flex items-center gap-3">
        <span className="text-xs font-mono uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
          Base URL
        </span>
        <code className="font-mono text-sm" style={{ color: 'var(--accent-primary)' }}>
          https://api.noyka.ai/api/v1
        </code>
      </div>

      {/* ── Authentication ── */}
      <SectionHeading id="authentication">Authentication</SectionHeading>
      <Paragraph>
        Noyka uses JWT bearer tokens for API authentication. Obtain a token by
        exchanging your API credentials at the token endpoint. All subsequent
        requests must include the token in the Authorization header.
      </Paragraph>

      {/* JWT Flow - 4 steps */}
      <SubHeading>JWT Authentication Flow</SubHeading>
      <div className="space-y-3 mb-6">
        {[
          { step: 1, title: 'Register application', desc: 'Create an application in the Noyka dashboard to get your client_id and client_secret.' },
          { step: 2, title: 'Request token', desc: 'POST your credentials to /auth/token with grant_type "client_credentials".' },
          { step: 3, title: 'Receive JWT', desc: 'The server returns a signed JWT with your scopes and a 1-hour expiry.' },
          { step: 4, title: 'Use token', desc: 'Include the JWT in the Authorization: Bearer header for all API calls.' },
        ].map((s) => (
          <div key={s.step} className="glass-card p-4 flex items-start gap-4">
            <div
              className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center font-mono text-sm font-bold"
              style={{
                background: 'rgba(0,212,255,0.1)',
                border: '1px solid rgba(0,212,255,0.2)',
                color: 'var(--accent-primary)',
              }}
            >
              {s.step}
            </div>
            <div>
              <h4 className="font-heading font-medium text-sm mb-1" style={{ color: 'var(--text-primary)' }}>
                {s.title}
              </h4>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{s.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <SubHeading>Obtain a JWT Token</SubHeading>
      <div className="flex items-center gap-2 mb-4">
        <span className="badge-post">POST</span>
        <code className="font-mono" style={{ fontSize: '15px', color: 'var(--text-secondary)' }}>/auth/token</code>
      </div>

      <ParamTable
        params={[
          { name: 'client_id', type: 'string', required: true, description: 'Your application client ID from the dashboard.' },
          { name: 'client_secret', type: 'string', required: true, description: 'Your application client secret. Keep this secure.' },
          { name: 'grant_type', type: 'string', required: true, description: 'Must be "client_credentials".' },
          { name: 'scope', type: 'string', required: false, description: 'Space-separated scopes. Default: "evidence:read evidence:write".' },
        ]}
      />

      <CodeBlock
        title="Request"
        examples={{
          curl: `curl -X POST https://api.noyka.ai/api/v1/auth/token \\
  -H "Content-Type: application/json" \\
  -d '{
    "client_id": "nyk_client_a1b2c3d4",
    "client_secret": "nyk_secret_••••••••",
    "grant_type": "client_credentials",
    "scope": "evidence:read evidence:write worm:write"
  }'`,
          python: `import requests

response = requests.post(
    "https://api.noyka.ai/api/v1/auth/token",
    json={
        "client_id": "nyk_client_a1b2c3d4",
        "client_secret": "nyk_secret_••••••••",
        "grant_type": "client_credentials",
        "scope": "evidence:read evidence:write worm:write",
    },
)
token = response.json()["access_token"]`,
          javascript: `const response = await fetch("https://api.noyka.ai/api/v1/auth/token", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    client_id: "nyk_client_a1b2c3d4",
    client_secret: "nyk_secret_••••••••",
    grant_type: "client_credentials",
    scope: "evidence:read evidence:write worm:write",
  }),
});
const { access_token } = await response.json();`,
          go: `payload := map[string]string{
    "client_id":     "nyk_client_a1b2c3d4",
    "client_secret": "nyk_secret_••••••••",
    "grant_type":    "client_credentials",
    "scope":         "evidence:read evidence:write worm:write",
}
body, _ := json.Marshal(payload)
resp, err := http.Post(
    "https://api.noyka.ai/api/v1/auth/token",
    "application/json",
    bytes.NewBuffer(body),
)`,
        }}
      />

      <CodeBlock
        title="Response"
        examples={{
          json: `{
  "access_token": "eyJhbGciOiJFUzI1NiIs...",
  "token_type": "Bearer",
  "expires_in": 3600,
  "scope": "evidence:read evidence:write worm:write",
  "issued_at": "2026-03-13T10:00:00Z"
}`,
        }}
      />

      <StatusCodes
        codes={[
          { code: '200', description: 'Token issued successfully.' },
          { code: '400', description: 'Invalid request body or missing required fields.' },
          { code: '401', description: 'Invalid client credentials.' },
          { code: '429', description: 'Rate limit exceeded. Retry after the Retry-After header value.' },
        ]}
      />

      {/* ── API Key Generation ── */}
      <SubHeading>API Key Generation</SubHeading>
      <Paragraph>
        For server-to-server integrations, you can generate long-lived API keys
        from the dashboard. API keys are prefixed with{' '}
        <code className="font-mono text-sm" style={{ color: 'var(--accent-primary)', opacity: 0.7 }}>nyk_live_</code> for
        production and{' '}
        <code className="font-mono text-sm" style={{ color: 'var(--accent-primary)', opacity: 0.7 }}>nyk_test_</code> for sandbox environments.
      </Paragraph>

      <CodeBlock
        title="Using API Key"
        examples={{
          curl: `curl https://api.noyka.ai/api/v1/evidence \\
  -H "Authorization: Bearer nyk_live_a1b2c3d4e5f6..." \\
  -H "X-Noyka-Version: 2026-03-01"`,
          python: `import requests

headers = {
    "Authorization": "Bearer nyk_live_a1b2c3d4e5f6...",
    "X-Noyka-Version": "2026-03-01",
}
response = requests.get(
    "https://api.noyka.ai/api/v1/evidence",
    headers=headers,
)`,
          javascript: `const response = await fetch("https://api.noyka.ai/api/v1/evidence", {
  headers: {
    Authorization: "Bearer nyk_live_a1b2c3d4e5f6...",
    "X-Noyka-Version": "2026-03-01",
  },
});`,
          go: `req, _ := http.NewRequest("GET", "https://api.noyka.ai/api/v1/evidence", nil)
req.Header.Set("Authorization", "Bearer nyk_live_a1b2c3d4e5f6...")
req.Header.Set("X-Noyka-Version", "2026-03-01")
resp, err := http.DefaultClient.Do(req)`,
        }}
      />

      {/* ── Base URL Configuration ── */}
      <SubHeading>Base URL Configuration</SubHeading>
      <Paragraph>
        All API requests should be made to the base URL. For sandbox testing,
        use the sandbox subdomain. Include the API version in the path.
      </Paragraph>
      <div className="glass-card p-4 my-4">
        <div className="space-y-2 font-mono text-sm">
          <div className="flex items-center gap-3">
            <span className="text-xs uppercase tracking-wider" style={{ color: 'var(--accent-success)' }}>Production</span>
            <code style={{ color: 'var(--text-primary)' }}>https://api.noyka.ai/api/v1</code>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs uppercase tracking-wider" style={{ color: 'var(--accent-warning)' }}>Sandbox</span>
            <code style={{ color: 'var(--text-primary)' }}>https://sandbox.api.noyka.ai/api/v1</code>
          </div>
        </div>
      </div>

      {/* ── Quick Start ── */}
      <SectionHeading id="quick-start">Quick Start</SectionHeading>
      <Paragraph>
        Follow these steps to make your first API call in under 2 minutes.
      </Paragraph>

      <div className="space-y-4">
        {[
          { step: 1, title: 'Get your credentials', desc: 'Sign up at dashboard.noyka.ai and create an application to receive your client_id and client_secret.' },
          { step: 2, title: 'Obtain a token', desc: 'Exchange credentials for a JWT bearer token using the /auth/token endpoint above.' },
          { step: 3, title: 'Make a request', desc: 'Use your token in the Authorization header to call any API endpoint.' },
          { step: 4, title: 'Handle responses', desc: 'All responses use standard HTTP status codes with JSON bodies. Errors include a machine-readable error code and human-readable message.' },
        ].map((s) => (
          <div key={s.step} className="glass-card p-4 flex items-start gap-4">
            <div
              className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center font-mono text-sm font-bold"
              style={{
                background: 'rgba(0,212,255,0.1)',
                border: '1px solid rgba(0,212,255,0.2)',
                color: 'var(--accent-primary)',
              }}
            >
              {s.step}
            </div>
            <div>
              <h4 className="font-heading font-medium text-sm mb-1" style={{ color: 'var(--text-primary)' }}>
                {s.title}
              </h4>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{s.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Error Response Format ── */}
      <SubHeading>Error Response Format</SubHeading>
      <Paragraph>
        All error responses follow a consistent format with a machine-readable code,
        human-readable message, optional details, and a request ID for debugging.
      </Paragraph>

      <CodeBlock
        title="Error Response"
        examples={{
          json: `{
  "error": {
    "code": "INVALID_TOKEN",
    "message": "The provided authentication token has expired.",
    "details": {
      "expired_at": "2026-03-13T09:00:00Z"
    },
    "request_id": "req_7f3a8b2c1d4e"
  }
}`,
        }}
      />

      <ParamTable
        params={[
          { name: 'error.code', type: 'string', required: true, description: 'Machine-readable error code (e.g., INVALID_TOKEN, RATE_LIMITED).' },
          { name: 'error.message', type: 'string', required: true, description: 'Human-readable error description.' },
          { name: 'error.details', type: 'object', required: false, description: 'Additional context about the error.' },
          { name: 'error.request_id', type: 'string', required: true, description: 'Unique request ID for support and debugging.' },
        ]}
      />

      {/* ── Rate Limits ── */}
      <SubHeading>Rate Limits</SubHeading>
      <Paragraph>
        API requests are rate-limited per application. Current limits are
        returned in response headers.
      </Paragraph>

      <ParamTable
        params={[
          { name: 'X-RateLimit-Limit', type: 'integer', required: false, description: 'Maximum requests per minute for your plan.' },
          { name: 'X-RateLimit-Remaining', type: 'integer', required: false, description: 'Remaining requests in the current window.' },
          { name: 'X-RateLimit-Reset', type: 'integer', required: false, description: 'Unix timestamp when the rate limit resets.' },
          { name: 'Retry-After', type: 'integer', required: false, description: 'Seconds to wait before retrying (only on 429 responses).' },
        ]}
      />
    </article>
  );
}
